import React from "react";
import {
  ScrollView,
  StyleSheet,
  View,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
} from "react-native";
import { NormalText } from "../../../../common/native";
import { TTemplateInfo } from "../../../../../utils/types";
import { convertATemplateWithHeightToAnotherHeight } from "../../../../../utils/functions";
import { mixpanel } from "../../../../../utils/mixpanel";
import { SessionContext } from "../../../../../contexts";
import Template from "../template/Template";

const TEMPLATE_HEIGHT = 150;

type TemplateListProps = {
  templatesInfo: TTemplateInfo[];
  selectedTemplateIndex: number | null;
  handleSelectTemplate: (index: number) => void;
  onTemplateDoubleClick: (index: number) => void;
};

const TemplateList: React.FC<TemplateListProps> = ({
  templatesInfo,
  selectedTemplateIndex,
  handleSelectTemplate,
  onTemplateDoubleClick,
}) => {
  const { width } = useWindowDimensions();
  const session = React.useContext(SessionContext)?.session;
  const lastClickRef = React.useRef<{ index: number | null; time: number }>({
    index: null,
    time: 0,
  });
  if (!session) return <p>Must have a session</p>;

  return (
    <ScrollView
      style={styles.scrollContainer}
      contentContainerStyle={[
        styles.imageCarouselContainer,
        { gap: width < 664 ? 10 : 40 },
      ]}
      showsVerticalScrollIndicator={true}
    >
      {templatesInfo.length === 0 && (
        <NormalText
          text={"No templates available"}
          style={{ marginHorizontal: "auto", marginTop: 50 }}
        />
      )}

      {templatesInfo.map((templateInfo, index) => {
        const currentTemplate = convertATemplateWithHeightToAnotherHeight(
          templateInfo.template,
          TEMPLATE_HEIGHT,
        );
        return (
          <View key={index} style={styles.templateContainer}>
            <TouchableOpacity
              accessibilityRole="button"
              accessibilityLabel={`Select ${templateInfo.templateDisplayName}`}
              accessibilityState={{
                selected: index === selectedTemplateIndex,
              }}
              {...({
                "aria-pressed": index === selectedTemplateIndex,
              } as any)}
              style={[
                styles.imageTouchopacity,
                index === selectedTemplateIndex && styles.selected,
              ]}
              onPress={() => {
                const now = Date.now();
                const isDoubleClick =
                  lastClickRef.current.index === index &&
                  now - lastClickRef.current.time < 300;
                handleSelectTemplate(index);
                if (isDoubleClick) {
                  onTemplateDoubleClick(index);
                }
                lastClickRef.current = { index, time: now };
                mixpanel.track("Template Selected", {
                  "Clinic Name": session?.clinicName ?? "clinic not found",
                });
              }}
            >
              <Template
                displayMode={true}
                swapImagesModalData={null}
                template={currentTemplate}
              />
            </TouchableOpacity>
            <NormalText text={templateInfo.templateDisplayName} />
          </View>
        );
      })}
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    width: "100%",
    flex: 1,
  },
  imageCarouselContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    maxWidth: 1040,
    width: "100%",
    marginHorizontal: "auto",
    paddingHorizontal: 10,
    justifyContent: "center",
    alignItems: "center",
    gap: 40,
  },
  templateContainer: {
    justifyContent: "center",
    alignItems: "center",
    gap: 5,
  },
  imageTouchopacity: {
    width: TEMPLATE_HEIGHT + 4,
    borderWidth: 2,
    borderColor: "#dddee0",
    borderRadius: 7,
    overflow: "hidden",
  },
  selected: {
    borderColor: "#00b7f4",
  },
});

export default TemplateList;
